import { useEffect } from 'react';

const useScrollUnlock = (enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const rootElement = document.getElementById('root');

    const originalHtmlStyle = html.style.cssText;
    const originalBodyStyle = body.style.cssText;
    const originalRootStyle = rootElement?.style.cssText || '';

    // UNLOCK means "get out of the way", NOT "add a scroller".
    //
    // 2026-08-24, Doug on a Pixel: could not scroll the contract at /sign/:token — a client
    // literally could not reach the signature field. Cause: this hook set
    // `overflow-y: auto` on html AND body AND #root, creating THREE nested scroll
    // containers. Mobile Chrome hands the touch gesture to one of them; the others have
    // nothing to scroll, so the page reads as frozen. Desktop hides it because a mouse
    // wheel bubbles between nested scrollers and touch does not.
    //
    // `overflow: visible` restores ordinary document flow — the viewport scrolls the way it
    // does on every normal web page, which is the behaviour that works everywhere. Never
    // reintroduce `overflow-y: auto` here; if a specific surface needs an inner scroller it
    // owns that itself, on its own element, not on the document root.
    const applyScrollStyles = (element) => {
      if (!element) return;
      element.style.height = 'auto';
      element.style.minHeight = '';
      element.style.maxHeight = 'none';
      // 2026-09-11: DO NOT set overflow-x here. Per CSS overflow spec, when one axis is
      // `visible` and the other is not, the `visible` one COMPUTES TO `auto`. So
      // `overflow-x: hidden` + `overflow-y: visible` silently produced `overflow-y: auto`
      // on html AND body AND #root — reinstating the exact three-nested-scroller bug the
      // 2026-08-25 fix below was written to kill. Nobody reintroduced `auto`; CSS did it.
      // Measured live on Alyssa Kurtz-Price's contract: 3 scroll containers, 2 of them with
      // nothing to scroll; setting overflow-x back to visible took it to 0.
      // Horizontal clipping belongs to the PAGE (ContractSigningPage already carries
      // `overflow-x-hidden` on its own wrapper), never to the document root.
      element.style.overflow = 'visible';
      element.style.position = 'static';
    };

    html.classList.add('allow-scroll');
    body.classList.add('allow-scroll');
    applyScrollStyles(html);
    applyScrollStyles(body);
    if (rootElement) {
      applyScrollStyles(rootElement);
    }

    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    return () => {
      html.classList.remove('allow-scroll');
      body.classList.remove('allow-scroll');
      html.style.cssText = originalHtmlStyle;
      body.style.cssText = originalBodyStyle;
      if (rootElement) {
        rootElement.style.cssText = originalRootStyle;
      }
    };
  }, [enabled]);
};

export default useScrollUnlock;

